import DateFormComponent from "components/shared/FormComponent/DateFormComponent";
import PriceFormComponent from "components/shared/FormComponent/PriceFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import StyledButton from "components/shared/StyledButton";
import TitleSeparation from "components/shared/TitleSeparation";
import { formatDateToIso, getCleanStartorEndDay } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "store/store";
import type { PressTemplateConfiguration, TermTemplate } from "store/termTemplatesStore";
import { createTermTemplate, updateTermTemplate } from "store/termTemplatesStore";

interface PressTermTemplateFormProps {
    mode: "new" | "edit";
    template?: TermTemplate;
    onClose: () => void;
    /**
     * When set, the created template becomes a framework contract for this beneficiary.
     */
    beneficiaryId?: number;
}

interface IPressTemplateData {
    name: string;
    folioPrice: number | string;
    workNature: string;
    startDate: Date | null;
    endDate: Date | null;
}

const PressTermTemplateForm = ({ mode, template, onClose, beneficiaryId }: PressTermTemplateFormProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { companyInfos } = useCompanySettings();
    const nameInputRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState(false);

    const existingConfig = template?.configuration as unknown as PressTemplateConfiguration | undefined;

    const defaultValues: IPressTemplateData = useMemo(
        () => ({
            name: mode === "edit" ? template?.name ?? "" : "",
            folioPrice: mode === "edit" ? ((existingConfig?.folioPrice ?? 0) / 100).toString() : "",
            workNature: mode === "edit" ? existingConfig?.workNature ?? "" : "",
            startDate:
                mode === "edit" && existingConfig?.startDate ? getCleanStartorEndDay(existingConfig.startDate) : null,
            endDate: mode === "edit" && existingConfig?.endDate ? getCleanStartorEndDay(existingConfig.endDate) : null,
        }),
        [mode, template, existingConfig],
    );

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<IPressTemplateData>({ defaultValues, mode: "all" });

    useEffect(() => {
        if (mode === "edit") reset(defaultValues);
    }, [defaultValues, mode, reset]);

    const isFrameworkContract = beneficiaryId !== undefined;

    const onSubmit = async (form: IPressTemplateData) => {
        if (!companyInfos) return;
        setIsLoading(true);

        const configuration: PressTemplateConfiguration = {
            folioPrice: Math.round(+form.folioPrice.toString().replace(",", ".") * 100),
            workNature: form.workNature ?? "",
            startDate: form.startDate instanceof Date ? formatDateToIso(form.startDate) : null,
            endDate: form.endDate instanceof Date ? formatDateToIso(form.endDate) : null,
        };

        const data = {
            name: form.name ?? "",
            companyId: companyInfos.id,
            configuration: configuration as unknown as TermTemplate["configuration"],
            ...(beneficiaryId ? { beneficiaryId } : {}),
        };

        const response =
            mode === "new"
                ? await dispatch(createTermTemplate({ data }))
                : template
                  ? await dispatch(updateTermTemplate({ templateId: template.id, data }))
                  : undefined;

        if (response && response.meta.requestStatus === "fulfilled") onClose();
        setIsLoading(false);
    };

    return (
        <form
            style={{ display: "flex", flexDirection: "column", gap: "1rem", padding: "0 24px 24px" }}
            onSubmit={handleSubmit(onSubmit)}
        >
            <TextFormComponent
                label={isFrameworkContract ? t("TermTemplates.FrameworkContractName") : t("TermTemplates.TemplateName")}
                control={control}
                controlName="name"
                required
                error={errors.name}
                ref={nameInputRef}
            />

            {isFrameworkContract && (
                <>
                    <TitleSeparation title={t("TermTemplates.ValidityPeriod")} />
                    <div style={{ display: "flex", gap: "1rem" }}>
                        <div style={{ flex: 1 }}>
                            <DateFormComponent
                                label={t("TermTemplates.StartDate")}
                                control={control}
                                controlName="startDate"
                                error={errors.startDate}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <DateFormComponent
                                label={t("TermTemplates.EndDate")}
                                control={control}
                                controlName="endDate"
                                error={errors.endDate}
                            />
                        </div>
                    </div>
                </>
            )}

            <TitleSeparation title={t("TermTemplates.PressConfiguration")} />

            <PriceFormComponent
                label={t("TermTemplates.PressFolioPrice")}
                placeholder=""
                control={control}
                controlName="folioPrice"
                error={errors.folioPrice}
            />

            <TextFormComponent
                label={t("TermTemplates.PressWorkNature")}
                placeholder=""
                control={control}
                controlName="workNature"
                error={errors.workNature}
                showOptionalMark
            />

            <StyledButton buttonType="primary" fullWidth type="submit" loading={isLoading}>
                {mode === "new"
                    ? isFrameworkContract
                        ? t("TermTemplates.CreateFrameworkContract")
                        : t("TermTemplates.CreateTemplate")
                    : t("TermTemplates.ConfirmChanges")}
            </StyledButton>
        </form>
    );
};

export default PressTermTemplateForm;
